import { BlogArticle, BlogArticleKey } from "@/app/types/BlogArticle";
import { ArticleBlockType } from "@/app/types/BlogArticleContent";
import { BlogCategory } from "@/app/types/BlogCategory";
import { BlogSlugs, PublicPaths } from "@/app/vars/Routes";

export const salesKnowledgeBaseArticle: BlogArticle = {
  key: BlogArticleKey.SALES_KNOWLEDGE_BASE,
  slug: BlogSlugs.SALES_KNOWLEDGE_BASE,
  categoryId: BlogCategory.CUSTOMER_KNOWLEDGE,
  date: "2026-09-29",
  readTime: 8,
  locales: {
    fr: {
      title: "Quand un commercial part : garder la connaissance client",
      excerpt:
        "Quand un commercial quitte l'équipe, le CRM reste, mais le contexte de chaque compte part avec lui. Voici ce qu'il faut garder et comment organiser la reprise.",
      description:
        "Ce qui part avec un commercial, pourquoi le CRM ne le retient pas, et un plan de reprise de portefeuille sur 30 jours pour le manager et le repreneur.",
      ctaTitle: "Gardez vos comptes quand l'équipe change",
      ctaText:
        "On rattache chaque échange à la fiche du client, on tient sa synthèse à jour et on garde ses engagements, pour que le prochain commercial reprenne là où le précédent s'est arrêté.",
      body: [
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Un commercial annonce son départ. Le préavis court, les passations se font en une réunion, et le portefeuille est réparti entre deux collègues. Le CRM est à jour, les opportunités ont leur montant et leur étape. Tout semble en ordre.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Trois semaines plus tard, le nouveau responsable d'un compte appelle son contact principal. Il repose des questions déjà posées, propose une date déjà refusée, ignore la remise promise en juin. Le client ne le dit pas toujours, mais il le note : on recommence de zéro avec lui.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Ce n'est pas un problème de rigueur. C'est un problème de support : l'essentiel de ce qu'un commercial sait d'un compte n'a jamais été écrit nulle part.",
        },
        {
          type: ArticleBlockType.H2,
          text: "Ce qui part avec un commercial",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Ce qui quitte l'entreprise le jour du départ, ce ne sont pas les coordonnées ni l'historique des étapes. Ce sont six choses que le commercial portait dans sa tête et qu'il utilisait à chaque appel.",
        },
        {
          type: ArticleBlockType.LIST,
          items: [
            "**Le contexte du compte.** Pourquoi ce client a commencé à chercher, ce qui avait échoué avant, ce qui a changé dans son organisation depuis.",
            "**Les promesses faites.** Une remise pour le renouvellement, une fonctionnalité évoquée pour le trimestre suivant, un rappel promis avant la fin du mois. Chacune engage l'entreprise, pas seulement la personne qui l'a faite.",
            "**Qui décide vraiment.** Le contact principal n'est pas toujours la personne qui signe, ni celle qui peut bloquer. Le commercial le sait parce qu'il l'a entendu, rarement parce qu'il l'a noté.",
            "**Ce qui a été dit sur le budget et le calendrier.** Une enveloppe débloquée en septembre, un gel des achats jusqu'à la clôture annuelle, une échéance liée à un autre projet.",
            "**Les objections déjà soulevées.** Et surtout la réponse qui avait convaincu. Reposer une objection déjà traitée donne au client l'impression de ne pas avoir été écouté.",
            "**La tonalité de la relation.** Un interlocuteur qui préfère un message court à un appel, un sujet sensible à ne pas aborder, une tension passée qu'on a réglée ensemble.",
          ],
        },
        {
          type: ArticleBlockType.LEAD,
          text: "Un portefeuille ne se transmet pas avec une liste de comptes. Il se transmet avec ce qui s'est dit sur chacun.",
        },
        {
          type: ArticleBlockType.H2,
          text: "Pourquoi les notes du CRM ne le retiennent pas",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Le CRM est conçu pour piloter un pipeline : des champs, des étapes, des montants, des dates. Il répond très bien à la question « où en est ce compte ? ». Il répond mal à la question « que s'est-il dit avec ce client ? ».",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Les notes libres existent, mais elles sont écrites après l'appel, vite, entre deux rendez-vous. Elles retiennent la conclusion et perdent le chemin : « intéressé, à relancer » au lieu de « attend l'accord de sa directrice financière, qui craint un coût d'intégration ».",
        },
        {
          type: ArticleBlockType.TABLE,
          head: ["Ce que le CRM contient", "Ce que les conversations contiennent"],
          rows: [
            [
              "Étape de l'opportunité : négociation",
              "« On doit repasser en comité, notre DAF veut voir le coût sur trois ans »",
            ],
            ["Montant estimé", "Le budget réel, et la ligne sur laquelle il est pris"],
            ["Contact principal", "Qui décide, qui influence, qui peut bloquer"],
            [
              "Date de clôture prévue",
              "La contrainte qui fixe cette date, et ce qui la ferait glisser",
            ],
            ["Note : « objection prix »", "L'objection exacte, et l'argument qui l'avait levée"],
            ["Prochaine tâche : relance", "Ce qui a été promis, par qui, et pour quand"],
          ],
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "La colonne de gauche dit où en est le compte. La colonne de droite dit comment le reprendre. C'est la seconde qui disparaît au départ d'un commercial.",
        },
        {
          type: ArticleBlockType.H2,
          text: "Ce que doit contenir une mémoire de compte utile",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Avant de choisir un outil ou un format, fixez ce qu'un repreneur doit trouver en ouvrant un compte. Cette liste sert de grille, que la mémoire soit un document partagé ou une fiche client.",
        },
        {
          type: ArticleBlockType.LIST,
          items: [
            "Les échanges eux-mêmes, rattachés au compte, et pas seulement leur résumé.",
            "Une synthèse courte de la situation actuelle, datée, qui dit ce qui a changé depuis la dernière fois.",
            "Les engagements en cours : qui s'est engagé, sur quoi, pour quelle date, et s'ils sont tenus.",
            "Les ventes passées et en cours, avec leur issue : gagnée, perdue, ouverte, et pourquoi.",
            "Les personnes du côté client et leur rôle réel dans la décision.",
            "Les objections déjà entendues et la réponse qui avait fonctionné.",
            "Ce qui a été dit sur le budget, le calendrier et le processus de décision.",
            "Les signaux de risque : un concurrent cité, une insatisfaction, un contact qui ne répond plus.",
          ],
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Si l'un de ces éléments n'existe que dans la mémoire d'une personne, c'est lui qui partira en premier.",
        },
        {
          type: ArticleBlockType.H2,
          text: "Documenter ou capturer",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "La réponse habituelle au risque de départ est de demander aux commerciaux d'écrire davantage : un compte rendu après chaque appel, une fiche de passation au moment de partir. C'est documenter. Cela fonctionne tant que l'équipe a le temps, et cesse de fonctionner précisément quand on en a besoin : un commercial qui part n'a ni le temps ni la motivation de rédiger dix fiches de compte complètes.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Capturer, c'est garder les conversations elles-mêmes, et en tirer ce qui compte au fil de l'eau. La mémoire se construit pendant que le commercial travaille, pas le jour où il s'en va.",
        },
        {
          type: ArticleBlockType.COMPARE,
          bad: {
            title: "Documenter",
            items: [
              "Dépend de la discipline de chacun",
              "Retient la conclusion, perd les mots du client",
              "Se fait après coup, souvent au dernier moment",
              "Une fiche de passation écrite en une soirée",
            ],
          },
          good: {
            title: "Capturer",
            items: [
              "Se fait à chaque échange, sans effort supplémentaire",
              "Garde ce que le client a réellement dit",
              "Reste vérifiable : on peut relire le passage",
              "Le compte est déjà prêt quand quelqu'un part",
            ],
          },
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Capturer ne dispense pas de toute écriture. Un commercial qui sait pourquoi un compte a basculé doit le dire. Mais l'écriture complète la source, elle ne la remplace plus.",
        },
        {
          type: ArticleBlockType.H2,
          text: "Organiser la reprise d'un portefeuille en 30 jours",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Le plan qui suit s'adresse au manager et au repreneur. Il suppose que le commercial sortant est encore là quelques jours, ce qui n'est pas toujours le cas ; s'il est déjà parti, commencez directement à l'étape deux.",
        },
        {
          type: ArticleBlockType.H3,
          text: "Jours 1 à 5 : trier le portefeuille",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Le manager classe les comptes en trois groupes : ceux avec une vente ouverte ou un renouvellement proche, ceux avec un engagement en cours, et les autres. Les deux premiers groupes sont repris en priorité. Avec le commercial sortant, passez vingt minutes par compte prioritaire sur une seule question : « qu'est-ce qui n'est écrit nulle part ? ».",
        },
        {
          type: ArticleBlockType.H3,
          text: "Jours 5 à 10 : relire avant d'appeler",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Le repreneur relit, pour chaque compte prioritaire, les derniers échanges et la synthèse. Il liste les engagements pris au nom de l'entreprise et vérifie ceux qui arrivent à échéance. Un engagement oublié pendant une passation est la première cause de perte de confiance.",
        },
        {
          type: ArticleBlockType.H3,
          text: "Jours 10 à 20 : le premier contact",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Le repreneur contacte chaque compte prioritaire, en montrant qu'il connaît le dossier. Il ne demande pas « où en êtes-vous ? » ; il dit ce qu'il sait et demande ce qui a changé. Par exemple : « J'ai vu que vous attendiez le comité de novembre pour valider le budget, est-ce que la date tient toujours ? ».",
        },
        {
          type: ArticleBlockType.H3,
          text: "Jours 20 à 30 : consolider",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Le manager et le repreneur font le point compte par compte : les engagements tenus, les ventes dont l'étape a bougé, les comptes silencieux depuis le départ. Les comptes du troisième groupe sont repris à leur tour, avec la même méthode.",
        },
        {
          type: ArticleBlockType.H2,
          text: "Les questions à savoir traiter avant le premier appel",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Un bon test de passation tient en cinq questions. Si le repreneur ne peut pas y répondre pour un compte, il n'est pas prêt à l'appeler.",
        },
        {
          type: ArticleBlockType.LIST,
          items: [
            "Pourquoi ce client nous a choisis, ou pourquoi il nous évalue ?",
            "Qu'est-ce qui a été promis, et pour quand ?",
            "Qui décide, et qui peut bloquer ?",
            "Quelle est la dernière objection soulevée, et comment a-t-elle été traitée ?",
            "Qu'est-ce qui pourrait faire partir ce client aujourd'hui ?",
          ],
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Ces réponses doivent pouvoir se vérifier. Une réponse de mémoire, transmise par oral, vaut une hypothèse. Une réponse qui renvoie au passage où le client l'a dit vaut un fait.",
        },
        {
          type: ArticleBlockType.H2,
          text: "Une fiche client qui se tient à jour toute seule",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "C'est le principe de la fiche client que nous proposons. Chaque échange y est rattaché, qu'il vienne d'un appel enregistré sur Google Meet avec l'extension Chrome ou d'un enregistrement importé. Après chaque échange, la synthèse du client est mise à jour, les engagements sont extraits avec leur responsable et leur échéance, et les ventes détectées dans la conversation apparaissent avec leur issue, modifiables à la main.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Le repreneur peut aussi poser ses cinq questions directement à l'assistant, qui répond à partir des échanges du compte et cite pour chaque affirmation le passage exact dont elle vient. Il relit la source au lieu de faire confiance à un résumé.",
        },
        {
          type: ArticleBlockType.CALLOUT,
          title: "La fiche client Meidly",
          text: "Tous les échanges d'un client au même endroit, une synthèse à jour, les engagements avec leurs échéances et les ventes en cours. Le prochain commercial reprend là où le précédent s'est arrêté.",
          linkLabel: "Voir les fiches clients",
          linkPath: PublicPaths.HOME,
        },
        {
          type: ArticleBlockType.H2,
          text: "Les limites à garder en tête",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Une mémoire de compte ne contient que ce qui a été capturé. Un échange qui n'a pas été enregistré, un déjeuner, un appel passé depuis un téléphone personnel, n'y figure pas. Enregistrer suppose aussi d'informer les participants et d'obtenir leur accord.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Et une synthèse générée reste un brouillon : elle se relit. Son intérêt n'est pas de remplacer le jugement du repreneur, mais de lui donner en quelques minutes la matière qu'il aurait mis des semaines à reconstituer.",
        },
        {
          type: ArticleBlockType.LEAD,
          text: "Le meilleur moment pour préparer un départ est le jour où le commercial arrive.",
        },
      ],
      faq: [
        {
          question: "Qu'est-ce qu'une base de connaissance commerciale ?",
          answer:
            "C'est l'endroit où l'équipe garde ce qui s'est dit avec chaque client : le contexte du compte, les engagements, les personnes qui décident, les objections et les ventes. Elle complète le CRM, qui suit les étapes et les montants, en gardant les conversations elles-mêmes.",
        },
        {
          question: "Combien de temps prévoir pour reprendre un portefeuille ?",
          answer:
            "Un mois est un ordre de grandeur raisonnable pour reprendre les comptes prioritaires : une semaine pour trier, une semaine pour relire, puis les premiers contacts et un point de consolidation. Les comptes sans vente ni engagement en cours peuvent attendre.",
        },
        {
          question: "Le CRM ne suffit-il pas pour une passation ?",
          answer:
            "Il suffit pour savoir où en est chaque compte. Il ne suffit pas pour savoir comment le reprendre : les promesses faites, le budget réel, qui décide et les objections déjà traitées vivent dans les conversations, rarement dans les champs.",
        },
        {
          question: "Faut-il demander au commercial sortant une fiche de passation ?",
          answer:
            "Oui, mais courte et ciblée sur ce qui n'est écrit nulle part. Une fiche complète rédigée pendant un préavis est rarement fiable. Garder les échanges au fil de l'eau évite de tout faire reposer sur ce moment.",
        },
        {
          question: "Peut-on enregistrer les appels clients pour les garder ?",
          answer:
            "Oui, à condition d'informer les participants et d'obtenir leur accord, et de définir une durée de conservation adaptée. Les échanges non enregistrés n'apparaîtront pas dans la mémoire du compte.",
        },
      ],
    },
    en: {
      title: "When a sales rep leaves: keeping customer knowledge",
      excerpt:
        "When a rep leaves the team, the CRM stays but the context of every account walks out with them. Here is what to keep and how to run the handover.",
      description:
        "What walks out with a departing sales rep, why the CRM does not hold it, and a 30 day portfolio handover plan for the manager and the new owner.",
      ctaTitle: "Keep your accounts when the team changes",
      ctaText:
        "We attach every exchange to the customer file, keep its synthesis up to date and track its commitments, so the next rep picks up where the last one left off.",
      body: [
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "A rep hands in their notice. The notice period runs, the handover happens in one meeting, and the portfolio is split between two colleagues. The CRM is up to date, the opportunities have their amount and their stage. Everything looks in order.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Three weeks later, the new owner of an account calls the main contact. They ask questions already asked, suggest a date already turned down, know nothing of the discount promised in June. The customer does not always say it, but they notice: they are starting from scratch.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "This is not a discipline problem. It is a medium problem: most of what a rep knows about an account was never written anywhere.",
        },
        {
          type: ArticleBlockType.H2,
          text: "What walks out the door with a rep",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "What leaves the company on the last day is not the contact details or the stage history. It is six things the rep carried in their head and used on every call.",
        },
        {
          type: ArticleBlockType.LIST,
          items: [
            "**The context of the account.** Why this customer started looking, what had failed before, what has changed in their organisation since.",
            "**The promises made.** A discount at renewal, a feature mentioned for next quarter, a call back promised before month end. Each one commits the company, not only the person who made it.",
            "**Who really decides.** The main contact is not always the person who signs, nor the one who can block. The rep knows because they heard it, rarely because they wrote it down.",
            "**What was said about budget and timing.** Budget released in September, a purchasing freeze until year end, a deadline tied to another project.",
            "**The objections already raised.** And above all the answer that convinced. Raising an objection that was already handled tells the customer they were not listened to.",
            "**The tone of the relationship.** A contact who prefers a short message to a call, a sensitive topic to avoid, a past tension that was settled together.",
          ],
        },
        {
          type: ArticleBlockType.LEAD,
          text: "A portfolio is not handed over with a list of accounts. It is handed over with what was said on each one.",
        },
        {
          type: ArticleBlockType.H2,
          text: "Why CRM notes do not hold it",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: 'A CRM is built to run a pipeline: fields, stages, amounts, dates. It answers "where is this account?" very well. It answers "what was said with this customer?" poorly.',
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: 'Free text notes exist, but they are written after the call, quickly, between two meetings. They keep the conclusion and lose the path: "interested, follow up" instead of "waiting for their CFO\'s approval, who is worried about the integration cost".',
        },
        {
          type: ArticleBlockType.TABLE,
          head: ["What the CRM holds", "What the conversations hold"],
          rows: [
            [
              "Opportunity stage: negotiation",
              '"We need to go back to committee, our CFO wants the three year cost"',
            ],
            ["Estimated amount", "The real budget, and the line it comes from"],
            ["Main contact", "Who decides, who influences, who can block"],
            ["Expected close date", "The constraint behind that date, and what would make it slip"],
            ['Note: "price objection"', "The exact objection, and the argument that resolved it"],
            ["Next task: follow up", "What was promised, by whom, and by when"],
          ],
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "The left column says where the account stands. The right column says how to pick it up. The second one is what disappears when a rep leaves.",
        },
        {
          type: ArticleBlockType.H2,
          text: "What a useful account memory must contain",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Before choosing a tool or a format, decide what a new owner must find when opening an account. This list works as a grid, whether the memory is a shared document or a customer file.",
        },
        {
          type: ArticleBlockType.LIST,
          items: [
            "The exchanges themselves, attached to the account, not only their summary.",
            "A short, dated synthesis of the current situation that says what changed since last time.",
            "Open commitments: who committed, to what, by which date, and whether they were kept.",
            "Past and current deals, with their outcome: won, lost, open, and why.",
            "The people on the customer side and their real role in the decision.",
            "The objections already heard and the answer that worked.",
            "What was said about budget, timing and the decision process.",
            "Risk signals: a competitor mentioned, dissatisfaction, a contact who stopped replying.",
          ],
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "If any of these only exists in one person's memory, it is the first thing to go.",
        },
        {
          type: ArticleBlockType.H2,
          text: "Documenting or capturing",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "The usual answer to the risk of departure is to ask reps to write more: a summary after every call, a handover sheet when they leave. That is documenting. It works as long as the team has time, and stops working exactly when you need it: a rep on their way out has neither the time nor the motivation to write ten complete account sheets.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Capturing means keeping the conversations themselves and drawing out what matters as you go. The memory builds while the rep works, not on the day they leave.",
        },
        {
          type: ArticleBlockType.COMPARE,
          bad: {
            title: "Documenting",
            items: [
              "Depends on each person's discipline",
              "Keeps the conclusion, loses the customer's words",
              "Done afterwards, often at the last minute",
              "A handover sheet written in one evening",
            ],
          },
          good: {
            title: "Capturing",
            items: [
              "Happens on every exchange, with no extra effort",
              "Keeps what the customer actually said",
              "Stays verifiable: you can reread the passage",
              "The account is already ready when someone leaves",
            ],
          },
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Capturing does not remove all writing. A rep who knows why an account turned should say so. But writing now complements the source, it no longer replaces it.",
        },
        {
          type: ArticleBlockType.H2,
          text: "Running a portfolio handover in 30 days",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "The plan below is for the manager and the new owner. It assumes the departing rep is still around for a few days, which is not always the case; if they have already left, start directly at step two.",
        },
        {
          type: ArticleBlockType.H3,
          text: "Days 1 to 5: sort the portfolio",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: 'The manager sorts the accounts into three groups: those with an open deal or an upcoming renewal, those with an open commitment, and the rest. The first two groups come first. With the departing rep, spend twenty minutes per priority account on a single question: "what is not written anywhere?".',
        },
        {
          type: ArticleBlockType.H3,
          text: "Days 5 to 10: read before calling",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "For each priority account, the new owner reads the latest exchanges and the synthesis. They list the commitments made on the company's behalf and check the ones coming due. A commitment forgotten during a handover is the first cause of lost trust.",
        },
        {
          type: ArticleBlockType.H3,
          text: "Days 10 to 20: the first contact",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: 'The new owner contacts each priority account and shows they know the file. They do not ask "where are you at?"; they say what they know and ask what has changed. For example: "I saw you were waiting for the November committee to approve the budget, is that date still holding?".',
        },
        {
          type: ArticleBlockType.H3,
          text: "Days 20 to 30: consolidate",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "The manager and the new owner review account by account: commitments kept, deals whose stage has moved, accounts that went quiet since the departure. The third group is then picked up in turn, with the same method.",
        },
        {
          type: ArticleBlockType.H2,
          text: "The questions to answer before the first call",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "A good handover test fits in five questions. If the new owner cannot answer them for an account, they are not ready to call it.",
        },
        {
          type: ArticleBlockType.LIST,
          items: [
            "Why did this customer choose us, or why are they evaluating us?",
            "What was promised, and by when?",
            "Who decides, and who can block?",
            "What was the last objection raised, and how was it handled?",
            "What could make this customer leave today?",
          ],
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "These answers must be verifiable. An answer from memory, passed on orally, is a hypothesis. An answer that points to the passage where the customer said it is a fact.",
        },
        {
          type: ArticleBlockType.H2,
          text: "A customer file that keeps itself up to date",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "That is the idea behind the customer file we offer. Every exchange is attached to it, whether it comes from a call recorded on Google Meet with the Chrome extension or from an uploaded recording. After each exchange, the customer synthesis is updated, commitments are extracted with their owner and due date, and deals detected in the conversation appear with their outcome, editable by hand.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "The new owner can also ask their five questions straight to the assistant, which answers from the account's exchanges and cites, for every claim, the exact passage it comes from. They reread the source instead of trusting a summary.",
        },
        {
          type: ArticleBlockType.CALLOUT,
          title: "The Meidly customer file",
          text: "Every exchange with a customer in one place, an up to date synthesis, commitments with their due dates and ongoing deals. The next rep picks up where the last one left off.",
          linkLabel: "See customer files",
          linkPath: PublicPaths.HOME,
        },
        {
          type: ArticleBlockType.H2,
          text: "The limits to keep in mind",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "An account memory only holds what was captured. An exchange that was not recorded, a lunch, a call from a personal phone, is not in it. Recording also requires informing the participants and obtaining their consent.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "And a generated synthesis is a draft: it gets reviewed. Its value is not to replace the new owner's judgement, but to give them in a few minutes the material they would have spent weeks rebuilding.",
        },
        {
          type: ArticleBlockType.LEAD,
          text: "The best time to prepare for a departure is the day the rep joins.",
        },
      ],
      faq: [
        {
          question: "What is a sales knowledge base?",
          answer:
            "It is where the team keeps what was said with each customer: the account context, commitments, who decides, objections and deals. It complements the CRM, which tracks stages and amounts, by keeping the conversations themselves.",
        },
        {
          question: "How long should a portfolio handover take?",
          answer:
            "A month is a reasonable order of magnitude to take over the priority accounts: a week to sort, a week to read, then the first contacts and a consolidation review. Accounts with no open deal or commitment can wait.",
        },
        {
          question: "Isn't the CRM enough for a handover?",
          answer:
            "It is enough to know where each account stands. It is not enough to know how to pick it up: promises made, the real budget, who decides and the objections already handled live in the conversations, rarely in the fields.",
        },
        {
          question: "Should the departing rep write a handover sheet?",
          answer:
            "Yes, but short and focused on what is not written anywhere. A complete sheet written during a notice period is rarely reliable. Keeping exchanges as you go avoids resting everything on that moment.",
        },
        {
          question: "Can customer calls be recorded to keep them?",
          answer:
            "Yes, provided the participants are informed and give their consent, and a suitable retention period is set. Exchanges that are not recorded will not appear in the account memory.",
        },
      ],
    },
  },
};
